import axios, { AxiosInstance } from "axios";
import _ from "lodash";
import { loadConfig } from "./config";
import history from "./history";
import { Auth, isAuthenticated } from "./auth";

const env = loadConfig();
// eslint-disable-next-line @typescript-eslint/no-var-requires
const messages = require("../constants/messages.json");

export const signOut = () => {
  ["auth", "user", "token", "secretKey", "latestInteractTime", "os"].forEach(
    (field) => {
      localStorage.removeItem(field);
    }
  );
};

export const redirectToLogin = () => {
  const pathname = window.location.pathname.replace("/", "");
  const hash = window.location.hash.replace("/", "");
  history.push("/login?prevpath=" + pathname + "&prevhash=" + hash);
};

const onFulfilled = (response) => {
  const status = _.get(response, "status", "");
  if (!status) {
    return Promise.reject(messages.invalidAPIResponse);
  }
  if (status >= 300 || status < 200) {
    const error = _.get(response, "message", messages.generalErrorMessage);
    return Promise.reject(error);
  }
  return _.get(response, "data", {});
};

const onError = () => {
  return Promise.reject(messages.generalErrorMessage);
};

// For APIs without authorization.
export const xapi = (() => {
  let api: AxiosInstance | null = null;
  return () => {
    if (!api) {
      api = axios.create({
        baseURL: env.apiHost,
        // timeout: env.apiTimeout,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      // Verify the response format
      api.interceptors.response.use(onFulfilled, onError);
    }
    return api;
  };
})();

// For APIs with authorization.
export const authXapi = (() => {
  let xapi: AxiosInstance | null = null;

  const getToken = async () => {
    let token = "";
    try {
      const session = await (await Auth()).currentSession();
      token = _.get(session, "idToken.jwtToken", "");
    } catch (e) {}

    return token;
  };

  const noToken = async () => (await getToken()).length < 10;

  return async (jsonFormat = true) => {
    if (
      !xapi ||
      xapi.defaults.headers.Authorization !== `Bearer ${getToken()}`
    ) {
      xapi = axios.create({
        baseURL: env.apiHost,
        // timeout: env.apiTimeout,
        headers: {
          ...(jsonFormat
            ? {
                // 'X-Requested-With': 'XMLHttpRequest',
                Accept: "application/json",
                "Content-Type": "application/json",
              }
            : {}),
          Authorization: `Bearer ${await getToken()}`,
        },
      });

      xapi.interceptors.response.use(
        async (res) => {
          // If authenticated but the token is lost, force sign out.
          if ((await isAuthenticated()) && (await noToken())) {
            signOut();
            redirectToLogin();
            return Promise.reject(messages.noTokenMessage);
          }
          return onFulfilled(res);
        },
        async (err) => {
          console.log("err", err);
          const status = _.get(err, "response.status", 200) + "";
          if (status === "401" || (await noToken())) {
            signOut();
            redirectToLogin();
            return Promise.reject(messages.generalErrorMessage);
          }
          return onError();
        }
      );
    }
    return xapi;
  };
})();

export const externalXapi = (baseURL) => {
  const api = axios.create({
    // timeout: env.apiTimeout,
    baseURL,
    headers: {},
  });
  api.interceptors.response.use((response) => {
    const status = _.get(response, "status", "");
    if (status !== 200) {
      const error = _.get(response, "message", messages.generalErrorMessage);
      return Promise.reject(error);
    }
    return _.get(response, "data", {});
  }, onError);
  return api;
};
